import i18n from '../../i18n';

/**
 * Central list of AI base regimes for the account + per-folder overrides.
 * Add new entries here when product defines more modes.
 */
export const AI_BASE_REGIME_OPTIONS: {
  value: string;
  label: string;
  description?: string;
  comingSoon?: boolean;
  beta?: boolean;
}[] = [
  { value: 'only_talk', label: i18n.t('chatAssistant:regimes.onlyTalk.label'), description: i18n.t('chatAssistant:regimes.onlyTalk.description') },
  { value: 'talk_and_tease', label: i18n.t('chatAssistant:regimes.talkAndTease.label'), description: i18n.t('chatAssistant:regimes.talkAndTease.description') },
  { value: 'talk_and_sext', label: i18n.t('chatAssistant:regimes.talkAndSext.label'), description: i18n.t('chatAssistant:regimes.talkAndSext.description') },
  {
    value: 'talk_sext_and_sales_ladder',
    label: i18n.t('chatAssistant:regimes.talkSextAndSalesLadder.label'),
    description: i18n.t('chatAssistant:regimes.talkSextAndSalesLadder.description'),
  },
  {
    value: 'talk_sext_and_hot_tip',
    label: i18n.t('chatAssistant:regimes.talkSextAndHotTip.label'),
    description: i18n.t('chatAssistant:regimes.talkSextAndHotTip.description'),
  },
];

/**
 * Regimes whose end the brain hands to a chatter only when "Call chatter" is on, with
 * the sexting step it ends at. The other regimes never read the flag: only_talk never
 * ends, the sales ladder and hot tip finish through their own policy settings.
 */
const COMPLETION_CHATTER_STEP: Readonly<Record<string, number>> = { talk_and_tease: 5, talk_and_sext: 10 };

export function regimeUsesCompletionChatter(regime: string): boolean {
  return regime in COMPLETION_CHATTER_STEP;
}

/** Sexting step the regime ends at, or null when it doesn't end by step. */
export function regimeCompletionStep(regime: string): number | null {
  return COMPLETION_CHATTER_STEP[regime] ?? null;
}

export interface RegimeSelectOption {
  value: string;
  label: string;
  description?: string;
  comingSoon?: boolean;
  beta?: boolean;
}

export function buildRegimeSelectOptions(currentValue: string): RegimeSelectOption[] {
  const combined: RegimeSelectOption[] = [...AI_BASE_REGIME_OPTIONS];
  const known = new Set(combined.map((o) => o.value));
  // Any account/override still holding a removed (legacy or first-PPV) value keeps
  // working: it shows as a read-only "Saved value" entry until re-saved to a current regime.
  if (currentValue && !known.has(currentValue)) {
    return [{ value: currentValue, label: i18n.t('chatAssistant:regimes.savedValue', { value: currentValue }) }, ...combined];
  }
  return combined;
}

export function resolveAccountBaseRegime(account: {
  ai_base_regime?: string | null;
  default_automation_regime?: string | null;
}): string {
  return account.ai_base_regime || account.default_automation_regime || 'talk_and_tease';
}
